import React, { useCallback } from 'react';
import type { Nullable, SignupFlow, StringMap } from 'corgi-types';
import { UserType } from 'graphql-ts-types';

import DynamicForm, { type OnWatchedFieldChange } from 'common/components/DynamicForm';
import { useFetchValidations } from 'common/components/DynamicForm/hooks/fetch-validations';
import FormSubmitButton from 'common/components/Form/FormSubmitButton';
import type { Slices } from 'common/components/HomeClubDrawer/types';
import { useLocalization } from 'common/providers/LocaleProvider';
import { Event } from 'common/types/auth';
import type { DispatchState } from 'common/types/React';
import { getIsReferralSignUpFlow } from 'common/utils/user-flow';
import type { ThirdPartySignUpData } from '../../types';
import { getAuthenticationSource } from '../../utils';
import { FieldName } from '../enums';
import { useFormGroups } from '../hooks/form-groups';
import { useFormSubmitButtonState } from '../hooks/perform-registration';
import { fetchRegistrationRules } from '../hooks/validation-rules';
import { getInitialFormData } from './utils';
import type { FormData as RegistrationFormData } from './utils';

interface FormProps {
  loginUrl: string;
  userType: UserType;
  isDisabled: boolean;
  userFlow: SignupFlow;
  forgotPasswordUrl: string;
  showHomeClubConsent: boolean;
  homeClubSlices: Nullable<Slices>;
  isThirdPartySignUpFlow: boolean;
  onFailedHomeClubOptIn: () => void;
  setIsDisabled: DispatchState<boolean>;
  setUserType: (userType: UserType) => void;
  setCommonError: (errorMessage: string) => void;
  onSuccess: ({
    queryParams,
    formData,
  }?: {
    queryParams?: StringMap;
    formData?: RegistrationFormData;
  }) => void;
  thirdPartySignUpData?: ThirdPartySignUpData;
  referredBy?: string;
}

const Form = ({
  loginUrl,
  userFlow,
  userType,
  onSuccess,
  isDisabled,
  referredBy,
  setUserType,
  setIsDisabled,
  homeClubSlices,
  setCommonError,
  forgotPasswordUrl,
  showHomeClubConsent,
  thirdPartySignUpData,
  onFailedHomeClubOptIn,
  isThirdPartySignUpFlow,
}: FormProps) => {
  const { t } = useLocalization();
  const validationRules = useFetchValidations(fetchRegistrationRules);

  const defaultButtonLabel = t('h24_registration_submit_button');
  const initialFormData = getInitialFormData({
    userType,
    ...thirdPartySignUpData,
  });

  const { handleSubmit, isSuccess } = useFormSubmitButtonState({
    userFlow,
    loginUrl,
    forgotPasswordUrl,
    tracking: {
      buttonLabel: defaultButtonLabel,
    },
    setCommonError,
    onSuccess,
    onFailedHomeClubOptIn,
    isThirdPartySignUpFlow,
    thirdPartySignUpData: isThirdPartySignUpFlow && {
      provider: thirdPartySignUpData.provider,
      providerToken: thirdPartySignUpData.providerToken,
    },
    ...(getIsReferralSignUpFlow(userFlow) && { referredBy }),
    setIsDisabled,
    validationRules,
  });

  const formGroups = useFormGroups({
    homeClubSlices,
    initialFormData,
    validationRules,
    showHomeClubConsent,
    isThirdPartySignUpFlow,
    isBusinessFormActive: userType === UserType.Business,
  });

  const onWatchedFieldChange: OnWatchedFieldChange<FieldName> = useCallback(
    (fieldName, value, methods) => {
      if (fieldName === FieldName.UserType) {
        setUserType(value === UserType.Business ? UserType.Business : UserType.Private);
        methods.setValue(FieldName.Password, '');
      }
    },
    []
  );

  return (
    <DynamicForm
      id="registration-form"
      formGroups={formGroups}
      onSubmit={handleSubmit}
      isDisabledWhileSubmitting
      fieldsToWatch={[FieldName.UserType]}
      onWatchedFieldChange={onWatchedFieldChange}
      ariaLabel={t('h24_registration_form')}
      renderSubmitButton={props => (
        <FormSubmitButton
          {...props}
          defaultButtonLabel={defaultButtonLabel}
          successButtonLabel={t('h24_user_has_registered_successfully')}
          isDisabled={props.isDisabled || isDisabled}
          isSuccess={isSuccess}
          eventName={Event.RegistrationFormClick}
          trackingParams={{
            provider: getAuthenticationSource(isThirdPartySignUpFlow, thirdPartySignUpData),
            customer_account_type: userType,
          }}
          marginTop="sp_16"
          marginBottom="sp_20"
          paddingTop="sp_12"
          paddingBottom="sp_12"
        />
      )}
    />
  );
};

export default Form;
